import cx from "classnames";
import {
  IonContent,
  IonLabel,
  IonPage,
  IonRadio,
  IonRadioGroup,
  IonTextarea,
} from "@ionic/react";
import { isEmpty } from "lodash";
import { useRef, useEffect } from "react";

import s from "./SurveyQuestionnaire.module.scss";
import { useWebUserProvider } from "../../CommonStores/user/provider";
import { useWebUserStore } from "../../CommonStores/user/users";
import brandLogo from "../../../Common/assets/logo/wl-woodmark-logo-rev.svg";
import { WLLoader } from "../../../Common/components/WLLoader/WLLoader";
import WLDialog from "../../components/WLDialog/WLDialog";
import { useWLConfigProvider } from "../../CommonStores/wlConfig/useWLConfigProvider";
import { useWLLoaderStore } from "../../../Common/components/WLLoader/store";
import {
  useSurveyQuestionnaireProvider,
  useSurveyQuestionnaireActionsProvider,
} from "./provider";
import { SurveyChallengeKeys } from "../../../Common/models/challengesAndRewards";
import { WLButton } from "../../../Common/components/WLButton/WLButton";
import {
  MultipleChoiceQuestion,
  EssayQuestion,
} from "../../../Common/domain/entities/features/surveyQuestionnaire/dto";
import RadioButtonOff from "../../../Common/assets/icons/survey/radio-button-off.svg?react";
import RadioButtonOn from "../../../Common/assets/icons/survey/radio-button-on.svg?react";
import WLToastError from "../../components/WLToastError/WLToastError";
import { getSectionIcon, getSectionLabel } from "./utils";
import { SurveySubmitConfirmationModal } from "./SurveySubmitConfirmationModal/SurveySubmitConfirmationModal";
import { SurveySuccessSection } from "./SurveySuccessSection/SurveySuccessSection";
import { useSurveyQuestionnaireStore } from "./store";

const SurveyQuestionnaire = () => {
  const contentRef = useRef<HTMLIonContentElement>(null);
  const searchParams = new URLSearchParams(window.location.search);
  const userId = searchParams.get("userId") as string;
  const challengeKeyParam = searchParams.get("challengeKey");
  const challengeKey: SurveyChallengeKeys =
    (challengeKeyParam as SurveyChallengeKeys) || "memberSurvey";

  useWLConfigProvider();

  const { inValidUserRequest, isLoadingProviders } = useWebUserProvider({
    userId,
  });

  const { surveyQuestionnaire, isLoadingSurveyQuestionnaire } =
    useSurveyQuestionnaireProvider(challengeKey);

  const { isLoading } = useWLLoaderStore();
  const { user } = useWebUserStore();

  const {
    toastError,
    setToastError,
    isConfirmationModalOpen,
    setIsConfirmationModalOpen,
    isSubmissionSuccess,
    currentSectionIndex,
  } = useSurveyQuestionnaireStore();

  const {
    currentSection,
    isLastSection,
    getAnswerValue,
    handleMultipleChoiceChange,
    handleEssayChange,
    handleContinue,
    handleSubmit,
    handleConfirmSubmit,
  } = useSurveyQuestionnaireActionsProvider(surveyQuestionnaire);

  // Scroll to top when section changes (after clicking Continue)
  const prevSectionIndexRef = useRef<number>(currentSectionIndex);
  useEffect(() => {
    // Only scroll if section index actually changed (not on initial mount)
    if (
      contentRef.current &&
      prevSectionIndexRef.current !== currentSectionIndex &&
      currentSectionIndex > 0
    ) {
      contentRef.current.scrollToTop(300);
    }
    prevSectionIndexRef.current = currentSectionIndex;
  }, [currentSectionIndex]);

  return (
    <IonPage className={s.page}>
      <IonContent
        ref={contentRef}
        className={cx(s.pageContent)}
        scrollY={true}
        fullscreen
      >
        <div className={s.body}>
          <img className={s.logo} src={brandLogo} alt="" loading="eager" decoding="async" />
          {!isLoadingProviders && !isLoading && inValidUserRequest ? (
            <div className={cx(s.invalid)}>
              <IonLabel>
                Invalid Membership link, please{" "}
                <a
                  className={s.contactLink}
                  href="https://winnerslocker.com.au/contact/"
                  target="_newTab"
                >
                  contact support
                </a>
              </IonLabel>
            </div>
          ) : !isLoadingProviders && !isLoading && surveyQuestionnaire ? (
            isSubmissionSuccess ? (
              <SurveySuccessSection />
            ) : (
              <div className={s.boxContent}>
                <div className={s.surveyTitle}>
                  <IonLabel className="win-header-1 winner-sans medium white ion-text-uppercase ion-text-center">
                    {surveyQuestionnaire.title || "Member Survey"}
                  </IonLabel>
                  <IonLabel className="win-header-5 gilroy regular blue-grey200_2 ion-text-center">
                    {surveyQuestionnaire.description ||
                      "We're all ears — tell us what you think. Your feedback keeps us on track."}
                  </IonLabel>
                </div>

                {surveyQuestionnaire.sections && (
                  <div className={s.surveyProgress}>
                    {surveyQuestionnaire.sections.map((section, index) => {
                      // Determine state: visited (before current), active (current), inactive (after current)
                      const state =
                        index < currentSectionIndex
                          ? "visited"
                          : index === currentSectionIndex
                            ? "active"
                            : "inactive";

                      const IconComponent = getSectionIcon(section.icon, state);
                      const isLast =
                        index === surveyQuestionnaire.sections.length - 1;
                      const labelText = getSectionLabel(section.icon);

                      return (
                        <div
                          key={`progress-${index}`}
                          className={s.progressItem}
                        >
                          <div className={s.progressIconContainer}>
                            <div className={s.progressIcon}>
                              <IconComponent className={s.icon} />
                            </div>
                            <IonLabel
                              className={cx(
                                {
                                  [s.progressLabelVisited]: state === "visited",
                                  [s.progressLabelActive]: state === "active",
                                  [s.progressLabelInactive]:
                                    state === "inactive",
                                },
                                "win-body winner-sans bold blue500 ion-text-uppercase"
                              )}
                            >
                              {labelText}
                            </IonLabel>
                          </div>
                          {!isLast && (
                            <div
                              className={cx(s.progressLine, {
                                [s.progressLineVisited]:
                                  index < currentSectionIndex,
                                [s.progressLineNotVisited]:
                                  index >= currentSectionIndex,
                              })}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className={s.surveyQuestionsContainerWrapper}>
                  <div className={s.surveyQuestionsContainer}>
                    {/* sections will switch base on the state section index */}
                    {currentSection && (
                      <div className={s.surveyQuestions}>
                        {currentSection.questions.map((question) => {
                          const answerKey = `${currentSectionIndex}-${question.questionIndex}`;
                          const answerValue = getAnswerValue(
                            currentSectionIndex,
                            question.questionIndex
                          );

                          if (question.type === "multipleChoice") {
                            const multipleChoiceQuestion =
                              question as MultipleChoiceQuestion;
                            return (
                              <div
                                key={`question-${question.questionIndex}`}
                                className={s.surveyQuestion}
                              >
                                <IonLabel className="win-header-4 winner-sans medium white ion-text-uppercase">
                                  {multipleChoiceQuestion.question}
                                </IonLabel>

                                <IonRadioGroup
                                  className={s.radioGroup}
                                  value={answerValue}
                                  onIonChange={(event) => {
                                    handleMultipleChoiceChange(
                                      currentSectionIndex,
                                      question.questionIndex,
                                      event.detail.value
                                    );
                                  }}
                                >
                                  {multipleChoiceQuestion.options.map(
                                    (option) => {
                                      // Compare values - ensure type consistency
                                      // getAnswerValue now properly handles 0 (no longer falsy)
                                      const isSelected =
                                        answerValue !== "" &&
                                        Number(answerValue) ===
                                          option.optionIndex;
                                      return (
                                        <div
                                          key={`option-${option.optionIndex}`}
                                          className={cx(s.radioOption, {
                                            [s.radioOptionSelected]: isSelected,
                                          })}
                                        >
                                          <IonRadio
                                            className={s.customRadio}
                                            value={option.optionIndex}
                                          />
                                          <div className={s.radioIcon}>
                                            {isSelected ? (
                                              <RadioButtonOn />
                                            ) : (
                                              <RadioButtonOff />
                                            )}
                                          </div>
                                          <div className={s.choiceMoreDetails}>
                                            <IonLabel className="win-header-5 gilroy medium white">
                                              {option.choiceText}
                                            </IonLabel>
                                            <IonLabel className="win-header-5 gilroy medium blue-grey200_2">
                                              {option.choiceMoreDetails}
                                            </IonLabel>
                                          </div>
                                        </div>
                                      );
                                    }
                                  )}
                                </IonRadioGroup>
                              </div>
                            );
                          } else {
                            const essayQuestion = question as EssayQuestion;
                            return (
                              <div
                                key={`question-${question.questionIndex}`}
                                className={s.surveyQuestion}
                              >
                                <IonLabel className="win-header-4 winner-sans medium white ion-text-uppercase">
                                  {essayQuestion.question}
                                </IonLabel>
                                <IonTextarea
                                  className={cx(
                                    s.textArea,
                                    "win-header-5 gilroy regular dark"
                                  )}
                                  value={answerValue as string}
                                  onIonInput={(event) => {
                                    handleEssayChange(
                                      currentSectionIndex,
                                      question.questionIndex,
                                      event.detail.value as string
                                    );
                                  }}
                                  placeholder="Type here"
                                  rows={6}
                                />
                              </div>
                            );
                          }
                        })}
                      </div>
                    )}

                    <WLButton
                      className="wl-body-6 red-submit-button ion-no-margin"
                      onClick={isLastSection ? handleSubmit : handleContinue}
                    >
                      {isLastSection ? "Submit" : "Continue"}
                    </WLButton>
                  </div>
                </div>
              </div>
            )
          ) : (
            <div className={s.loadingContainer}>
              <WLLoader />
            </div>
          )}

          <div className={s.servicePolicy}>
            <IonLabel className="win-small gilroy medium blue-grey200_2 ion-text-center">
              <span>
                We handle your personal information as set out in our{" "}
              </span>
              <a
                href="https://winnerslocker.com.au/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className={s.link}
              >
                Privacy Policy
              </a>
              <span>.</span>
            </IonLabel>
          </div>
        </div>
      </IonContent>
      <WLLoader />
      <WLDialog />
      <WLToastError
        isOpen={!isEmpty(toastError)}
        duration={3000}
        message={toastError}
        onDidDismiss={() => {
          setToastError("");
        }}
      />
      <SurveySubmitConfirmationModal
        isOpen={isConfirmationModalOpen}
        onDismiss={() => setIsConfirmationModalOpen(false)}
        onGoBack={() => setIsConfirmationModalOpen(false)}
        onSubmit={handleConfirmSubmit}
      />
    </IonPage>
  );
};

export default SurveyQuestionnaire;
